import React, {useContext, useRef, useState} from 'react';
import {payForUploadedImageRequest} from 'util/api';
import {useDropzone} from 'react-dropzone';
import 'styles/pictures/UploadPage.css';
import ImageDisplay from 'components/pictures/display/ImageDisplay';
import SvgPriceTag from 'util/svg/createDynamicPricetag';
import AddMoneyPopupWrapper from 'components/donate/AddMoneyPopupWrapper';
import {usePopup} from 'popup/PopupContext';
import {UserContext} from 'components/user/UserContext';
import UniversalEmptyPageHeader from 'components/universal/UniversalEmptyPageHeader';

const UploadPage = () => {
    const [amount, setAmount] = useState(1);
    const [file, setFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [isSendDisabled, setIsSendDisabled] = useState(false);
    const [showDropzone, setShowDropzone] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const timeoutRef = useRef(null);
    const {openPopup} = usePopup();
    const {user} = useContext(UserContext);

    const [showErrors, setShowErrors] = useState(false);

    const MAX_FILE_SIZE_MB = 4;

    const onDrop = (acceptedFiles) => {
        const selectedFile = acceptedFiles[0];
        if (!selectedFile) return;

        setErrorMessage('');
        setIsSendDisabled(false);

        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                let {width, height} = img;
                let aspectRatio = width / height;

                if (width < 128 || height < 128) {
                    setErrorMessage('Image too small.');
                    setIsSendDisabled(true);
                    return;
                }

                // Compute the scale factor based on the shorter dimension
                let scale = 1;
                if (width > 1080 && height > 1080) {
                    if (width > height) {
                        scale = 1080 / height;
                    } else {
                        scale = 1080 / width;
                    }
                }

                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';

                // Initialize variables for cropping
                let cropWidth = width;
                let cropHeight = height;
                let sx = 0;
                let sy = 0;

                // Aspect ratio corrections
                if (aspectRatio < 9 / 16) {
                    // Image is too tall, crop height to match 9:16 aspect ratio
                    cropHeight = width * (16 / 9);
                    sy = (height - cropHeight) / 2;
                    setErrorMessage('Cropped to 9:16 ratio');
                } else if (aspectRatio > 16 / 9) {
                    // Image is too wide, crop width to match 16:9 aspect ratio
                    cropWidth = height * (16 / 9);
                    sx = (width - cropWidth) / 2;
                    setErrorMessage('Cropped to 16:9 ratio');
                }

                // Apply scaling to the cropped dimensions
                const scaledWidth = Math.floor(cropWidth * scale);
                const scaledHeight = Math.floor(cropHeight * scale);

                // Set canvas dimensions to the scaled size
                canvas.width = scaledWidth;
                canvas.height = scaledHeight;

                // Fill the canvas with white to replace transparent background
                ctx.fillStyle = '#FFFFFF';
                ctx.fillRect(0, 0, canvas.width, canvas.height);

                // Draw the cropped and scaled image onto the canvas
                ctx.drawImage(
                    img,
                    sx,
                    sy,
                    cropWidth,
                    cropHeight,
                    0,
                    0,
                    scaledWidth,
                    scaledHeight
                );

                canvas.toBlob(
                    (blob) => {
                        if (blob.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
                            setErrorMessage('Image too large.');
                            setIsSendDisabled(true);
                            return;
                        }

                        const fileUrl = URL.createObjectURL(blob);
                        setFile(blob);
                        setImagePreview(fileUrl);
                        setShowDropzone(false);
                    },
                    selectedFile.type
                );
            };
            img.src = e.target.result;
        };
        reader.readAsDataURL(selectedFile);
    };

    const handleCancel = () => {
        setFile(null);
        setImagePreview(null);
        setShowDropzone(true);
    };

    const handleSubmit = async () => {
        if (!file || !amount) {
            setShowErrors(true);
            if (timeoutRef.current) clearTimeout(timeoutRef.current); // Clear existing timeout
            timeoutRef.current = setTimeout(() => setShowErrors(false), 2000); // Set new timeout
            return;
        }

        const centAmount = parseInt(amount * 100);
        if (user.balance < centAmount) {
            openPopup(<AddMoneyPopupWrapper initialAmount={centAmount - user.balance} openInNewTab={true}/>);
            return;
        }

        setIsLoading(true);

        try {
            const session = await payForUploadedImageRequest(file, centAmount);
            window.location.href = session.url;
        } catch (error) {
            console.error('Error:', error);
            alert('Failed to create checkout session. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const {getRootProps, getInputProps} = useDropzone({
        accept: 'image/*',
        onDrop,
    });

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            handleSubmit();
        }
    };


    return (
        <div>
            {isLoading && (
                <div className="uploading-overlay">
                    <div className="uploading-popup">Uploading...</div>
                </div>
            )}
            <UniversalEmptyPageHeader image={'plus'} text={'Upload Image'} desktopText={true}/>
            <div className="upload-container">
                <div style={{display: 'flex', justifyContent: 'flex-end', width: '100%'}}>
                    <div style={{position: 'relative', width: '120px', textAlign: 'right'}}>
                        <SvgPriceTag gapSize={22}/>
                        <div className="price-tag-inside-input-container" style={{paddingBottom: '8px'}}>
                            <div
                                className={'price-tag-input-div' + (showErrors && !amount ? ' errored' : '')}
                                style={{textAlign: 'right', marginRight: '25px'}}
                            >
                                $
                                <input
                                    value={amount}
                                    className="price-tag-input"
                                    type="text"
                                    inputMode="numeric"
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setShowErrors(false);

                                        if (
                                            value === '' ||
                                            (/^[1-9]\d*$/.test(value) && Number(value) <= 999_999)
                                        ) {
                                            setAmount(value);
                                        }
                                    }}
                                    onKeyDown={handleKeyDown}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                {showDropzone ? (
                    <div {...getRootProps()} className={'dropzone-container' + (showErrors && !file ? ' errored' : '')}>
                        <input {...getInputProps()} disabled={isLoading}/>
                        <p>Drag 'n' drop an image here, or click to select one</p>
                    </div>
                ) : (
                    <>
                        {imagePreview && (
                            <div style={{position: 'relative'}}>
                                <ImageDisplay imageBlob={imagePreview} pic={{title: 'Image Preview'}}
                                              paddingTopPercent={56.25}/>
                            </div>
                        )}
                    </>
                )}
            </div>
            {errorMessage && <div style={{color: 'RED', fontSize: '14px'}}>{errorMessage}</div>}
            <div className={'upload-input-border'}>
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                    {imagePreview && (
                        <button type="button" className={'upload-small-button cancel'} onClick={handleCancel}
                                disabled={isLoading}>
                            Cancel
                        </button>
                    )}
                    <div/>
                    <button onClick={handleSubmit} disabled={isSendDisabled || isLoading}
                            className={'upload-small-button release'}>
                        Upload
                    </button>
                </div>
            </div>
        </div>
    );
};

const WrappedUploadPage = () => <UploadPage/>;

export default WrappedUploadPage;
